"use client";

import { useEffect, useRef, useState } from "react";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Perspectives() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const testimonials = [{
    quote: "ITLS transformed our operational strategy with precision. Their team provided the clarity we needed to scale effectively without compromising our core values.",
    name: "Sarah Jenkins",
    meta: "Sarah Jenkins, Director of Operations — two years with ITLS",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_sarah_portrait.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_sarah_portrait.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_sarah_portrait.webp-wvc-sizes",
      alt: "Portrait of Sarah Jenkins, an ITLS client"
    }
  }, {
    quote: "The level of personalized attention is unmatched. ITLS doesn't just offer solutions; they build a roadmap that aligns perfectly with our long-term vision.",
    name: "Marcus Thorne",
    meta: "Marcus Thorne, CEO — three years with ITLS",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_marcus_portrait.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_marcus_portrait.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_marcus_portrait.webp-wvc-sizes",
      alt: "Portrait of Marcus Thorne, an ITLS client"
    }
  }, {
    quote: "Working with ITLS has been a masterclass in efficiency. Their consulting approach is transparent, innovative, and consistently delivers measurable results.",
    name: "Elena Rodriguez",
    meta: "Elena Rodriguez, Principal Consultant — three years with ITLS",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_elena_portrait.webp",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_elena_portrait.webp-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1641356/img/client_elena_portrait.webp-wvc-sizes",
      alt: "Portrait of Elena Rodriguez, an ITLS client"
    }
  }];
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  useEffect(() => {
    if (!api || isPaused) return;
    intervalRef.current = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 9000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [api, isPaused]);
  return <section data-section-id="14784" id="client-perspectives" className="bg-background text-foreground py-28 md:py-36 px-6 md:px-12 font-default">
      <div ref={ref} className={`max-w-[1240px] mx-auto transition-all duration-[900ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
      transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
    }}>
        {}
        <div className="max-w-[720px] mx-auto text-center">
          <p className="font-default text-[13px] font-medium uppercase text-muted-foreground" style={{
          letterSpacing: "0.08em"
        }}>
            Client Perspectives
          </p>
          <h2 className="font-default mt-5 text-foreground" style={{
          fontSize: "clamp(2.1rem, 3.8vw, 3.5rem)",
          fontWeight: 500,
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            Partnerships built on{" "}
            <span className="relative inline-block">
              excellence
              <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 200 8" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" aria-hidden="true">
                <path d="M2 5 Q 25 1, 50 5 T 100 5 T 150 5 T 198 5" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" fill="none" style={{
                strokeDasharray: 220,
                strokeDashoffset: isVisible ? 0 : 220,
                transition: "stroke-dashoffset 900ms ease-out 600ms"
              }} />
              </svg>
            </span>
            .
          </h2>
        </div>

        {}
        <div className="mt-16 max-w-[1040px] mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} onFocus={() => setIsPaused(true)} onBlur={() => setIsPaused(false)}>
          <Carousel setApi={setApi} opts={{
          loop: true,
          duration: 70
        }} className="relative">
            <CarouselContent>
              {testimonials.map((t, i) => <CarouselItem data-index={i} key={i}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
                    {}
                    <div className="md:col-span-6">
                      <div className="overflow-hidden bg-card border border-border" style={{
                    borderRadius: "0px",
                    aspectRatio: "4 / 5"
                  }}>
                        <img src={t.image.src} data-wvc-srcset={t.image.srcset} data-wvc-sizes={t.image.sizes} alt={t.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[600ms] ease-out hover:scale-[1.03]" />
                      </div>
                    </div>

                    {}
                    <div className="md:col-span-6 flex flex-col justify-center">
                      <div aria-hidden="true" className="font-default text-primary/70 leading-none" style={{
                    fontSize: "80px"
                  }}>
                        &ldquo;
                      </div>
                      <blockquote className="font-default text-foreground -mt-4" style={{
                    fontSize: "clamp(1.25rem, 1.8vw, 1.5rem)",
                    fontWeight: 400,
                    lineHeight: 1.4,
                    letterSpacing: "-0.012em"
                  }}>
                        {t.quote}
                      </blockquote>
                      <p className="font-default mt-8 text-muted-foreground text-[15px]">
                        {t.meta}
                      </p>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <CarouselPrevious className="hidden md:flex -left-4 md:-left-16 size-10 border border-border bg-background text-foreground hover:bg-accent" />
            <CarouselNext className="hidden md:flex -right-4 md:-right-16 size-10 border border-border bg-background text-foreground hover:bg-accent" />
          </Carousel>

          {}
          <div className="flex items-center justify-center gap-2 mt-10">
            {testimonials.map((_, i) => <button key={i} data-index={i} onClick={() => api?.scrollTo(i)} aria-label={`Go to testimonial ${i + 1}`} className={`h-2 rounded-none transition-all duration-[400ms] ease-out ${current === i ? "w-6 bg-primary" : "w-2 bg-border hover:bg-muted-foreground/60"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
          }} />)}
          </div>
        </div>
      </div>
    </section>;
}